import { Building2, Compass, GalleryVerticalEnd, Handshake, Home, Info, Mail, Users } from "lucide-react";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "./ui/breadcrumb";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarRail, SidebarTrigger } from "./ui/sidebar";
import { useUser } from "@rhino-project/core/hooks";
import { useSignOutAction } from "@rhino-project/core/queries";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import GlobalFooter from "./GlobalFooter";
import GlobalHeader from "./GlobalHeader";

const coreNav = [
  { label: "Home", href: "/", icon: Home },
  { label: "Dual-Use On-Ramp", href: "/dual-use-on-ramp", icon: GalleryVerticalEnd },
  { label: "Sponsor View", href: "/sponsor-view", icon: Users, roles: ["capability_sponsor"] },
  { label: "Navigator Role", href: "/navigator-role", icon: Compass, roles: ["navigator"] },
  { label: "Opportunities", href: "/opportunities", icon: Compass },
];

const ecosystemNav = [
  { label: "Resources", href: "/resources", icon: Handshake },
  { label: "Strategic Partners", href: "/strategic-partners", icon: Handshake },
  { label: "DND Showcase", href: "/dnd-showcase", icon: Building2 },
];

const infoNav = [
  { label: "About", href: "/about", icon: Info },
  { label: "Contact", href: "/contact", icon: Mail },
];

const allNav = [...coreNav, ...ecosystemNav, ...infoNav];

export default function Layout() {
  const { pathname } = useLocation();
  const user = useUser();
  const { mutate: signOut } = useSignOutAction();
  const userRoles = (user?.users_roles ?? []).map((entry: any) => entry.role?.name).filter(Boolean);
  const canSee = (item: any) => !item.roles?.length || item.roles.some((role: string) => userRoles.includes(role));
  const visibleCoreNav = coreNav.filter(canSee);
  const initials = (user?.name || user?.email || "TD").split(/\s+|@/).filter(Boolean).slice(0, 2).map((part: string) => part[0]?.toUpperCase()).join("") || "TD";
  const topMatch = allNav.find((item) => pathname === item.href);
  const sectionLabel = pathname.startsWith("/resources/")
    ? "Resources"
    : pathname.startsWith("/dual-use-on-ramp/")
      ? "Dual-Use On-Ramp"
      : pathname.startsWith("/sponsor-view/")
        ? "Sponsor View"
        : pathname.startsWith("/navigator-role/")
          ? "Navigator Role"
          : pathname.startsWith("/opportunities/")
            ? "Opportunities"
            : topMatch?.label ?? "Workspace";

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full min-w-0 bg-background/70 text-foreground backdrop-blur-[1px]">
        <GlobalHeader />
        <div className="flex w-full min-w-0">
          <Sidebar collapsible="icon" variant="inset" className="border-r border-sidebar-border" aria-label="Primary navigation">
            <SidebarHeader>
              <Link to="/" className="flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold text-sidebar-foreground">
                <Building2 className="h-4 w-4 text-sidebar-primary" />
                <span>The Third Door</span>
              </Link>
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupLabel>Core</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {visibleCoreNav.map((item) => (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton asChild isActive={pathname === item.href} tooltip={item.label}>
                          <NavLink to={item.href}>
                            <item.icon />
                            <span>{item.label}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarGroup>
                <SidebarGroupLabel>Ecosystem</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {ecosystemNav.map((item) => (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton asChild isActive={pathname === item.href} tooltip={item.label}>
                          <NavLink to={item.href}>
                            <item.icon />
                            <span>{item.label}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarGroup>
                <SidebarGroupLabel>Info</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {infoNav.map((item) => (
                      <SidebarMenuItem key={item.href}>
                        <SidebarMenuButton asChild isActive={pathname === item.href} tooltip={item.label}>
                          <NavLink to={item.href}>
                            <item.icon />
                            <span>{item.label}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
            <SidebarFooter>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton>
                    <Avatar className="h-6 w-6">
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <span>Account</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="bg-popover text-popover-foreground">
                  {user ? (
                    <DropdownMenuItem onClick={() => signOut()}>Sign out</DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/login">Sign in</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/register">Create account</Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarFooter>
            <SidebarRail />
          </Sidebar>

          <SidebarInset className="min-w-0">
            <div className="sticky top-14 z-30 flex items-center gap-3 border-b border-border/80 bg-background/80 px-4 py-3 shadow-sm backdrop-blur-xl sm:px-6 lg:px-8">
              <SidebarTrigger />
              <Separator orientation="vertical" className="h-5" />
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/">Home</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{sectionLabel}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            <ScrollArea className="h-[calc(100svh-7.5rem)]">
              <div className="w-full min-w-0 px-4 py-6 sm:px-6 lg:px-8">
                <Outlet />
              </div>
              <GlobalFooter />
            </ScrollArea>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
}